/**
 * Signoz Configuration
 * Custom configuration untuk filtering error yang tidak perlu di-trace
 */

// Daftar error yang tidak perlu di-trace ke Signoz
export const IGNORED_ERRORS = [
  // Network errors
  "Network Error",
  "timeout",
  "Failed to fetch",
  "TypeError: Failed to fetch",
  "NetworkError",
  "ERR_NETWORK",
  "ERR_CONNECTION_REFUSED",
  "ERR_CONNECTION_RESET",
  "ERR_CONNECTION_TIMED_OUT",

  // Socket errors
  "Socket",
  "WebSocket",
  "disconnect",
  "reconnect",
  "connection error",

  // Bidding errors yang expected
  "Anda kalah cepat",
  "NIPL anda telah habis",
  "Tidak Bisa Bid",
  "Bid Anda sudah paling atas",
  "kalah cepat",

  // Browser/User errors
  "ResizeObserver",
  "Non-Error promise rejection",
  "cancelled",
  "aborted",
  "blob:",
  "blob:http",
  "blob:https",

  // React errors yang tidak critical
  "Minified React error",

  // Auction specific errors
  "Lelang belum dimulai",
  "Lelang telah berakhir",

  // Third-party script errors (ContentSquare, Analytics, etc)
  "contentsquare.net",
  "this.o.at is not a function",
  "t.contentsquare.net",
  "uxa/",

  // Google Tag Manager / Analytics errors
  "google-analytics",
  "googletagmanager",
  "gtag/js",
  "www.googletagmanager.com",
  "GA4React",
  "GA4React intialization failed",
  "GA4React initialization failed",

  // reCAPTCHA errors
  "reCAPTCHA",
  "recaptcha",
  "gstatic.com/recaptcha",
  "reCAPTCHA Timeout",
  "reCAPTCHA placeholder element",
  "reCAPTCHA has already been rendered",

  // Google Ads / Publisher Tag errors
  "googletag",
  "googletag.pubads",
  "TossPixel",
  "Cannot read properties of undefined (reading 'push')",
  "Cannot read property 'push' of undefined",
  "Cannot read properties of undefined (reading 'dataLayer')",
  "Cannot read property 'dataLayer' of undefined",
  "_gaq",
  "_paq",
  "criteo_q",
  "adroll",

  // Other third-party errors
  "facebook.net",
  "doubleclick.net",
  "Script error",
  "Script error.",

  // Firebase Cloud Messaging errors
  "FirebaseError: Messaging:",
  "messaging/token-update-failed",
  "messaging/token-subscribe-failed",
  "messaging/token-unsubscribe-failed",
  "messaging/permission-blocked",
  "messaging/permission-default",
  "messaging/notifications-blocked",
  "messaging/failed-service-worker-registration",
  "A problem occurred while updating the user from FCM",
  "Failed to update token",
  "firebase.com",
  "firebaseio.com",
  "googleapis.com",
  "fcm.googleapis.com",
  "Failed to register a ServiceWorker",
  "ServiceWorker",
  "firebase-messaging-sw.js",
  "serviceWorkers.navigator.serviceWorker.register",
  "Error: Rejected",

  // SigNoz / OpenTelemetry internal errors
  "BatchSpanProcessorBase",
  "BatchSpanProcessor",
  "Unhandled Promise rejection: Timeout",
  "zone.js",
  "at timer (zone.js",
  "_ZoneDelegate.invokeTask",
  "Zone.runTask",
  "ZoneTask.invoke",
  "runTask@",
  "invokeTask@",
  "ZoneDelegate",
  "Zone.run",
];

// Daftar URL yang tidak perlu di-trace
export const IGNORED_URLS = ["/api/v2/auction/", "/api/v3/auction/", "/broadcasting/auth"];

// Daftar kombinasi URL + Status Code yang tidak perlu di-trace
export const IGNORED_URL_STATUS = [
  { url: "/api/login", status: 403 }, // Login 403 Forbidden - expected error
  { url: "/api/login", status: 401 }, // Login 401 Unauthorized - expected error
];

/**
 * Check if error should be ignored
 * @param {Error|string} error - Error object or message
 * @returns {boolean} - True if error should be ignored
 */
export const shouldIgnoreError = (error) => {
  if (!error) return true;

  const errorMessage = typeof error === "string" ? error : error.message || error.toString();
  const errorStack = error?.stack || "";

  // Check if error message contains any ignored patterns
  const messageMatch = IGNORED_ERRORS.some((pattern) => errorMessage.toLowerCase().includes(pattern.toLowerCase()));

  // Check if error stack contains third-party domains
  const stackMatch =
    errorStack &&
    (errorStack.includes("contentsquare.net") ||
      errorStack.includes("google-analytics") ||
      errorStack.includes("googletagmanager") ||
      errorStack.includes("gtag/js") ||
      errorStack.includes("facebook.net") ||
      errorStack.includes("doubleclick.net") ||
      errorStack.includes("firebase.com") ||
      errorStack.includes("firebaseio.com") ||
      errorStack.includes("googleapis.com") ||
      errorStack.includes("fcm.googleapis.com"));

  // Special check for GTM "Failed to fetch" errors
  const isGTMFetchError =
    errorMessage.includes("Failed to fetch") &&
    (errorStack.includes("googletagmanager.com") ||
      errorStack.includes("gtag/js") ||
      errorStack.includes("google-analytics.com"));

  // Special check for SigNoz/OpenTelemetry internal errors
  const isSigNozInternalError =
    errorStack.includes("BatchSpanProcessorBase") ||
    errorStack.includes("BatchSpanProcessor") ||
    (errorMessage.includes("Timeout") && errorStack.includes("zone.js")) ||
    errorStack.includes("_ZoneDelegate.invokeTask") ||
    errorStack.includes("Zone.runTask") ||
    errorStack.includes("ZoneTask.invoke");

  return messageMatch || stackMatch || isGTMFetchError || isSigNozInternalError;
};

/**
 * Check if URL should be ignored
 * @param {string} url - Request URL
 * @returns {boolean} - True if URL should be ignored
 */
export const shouldIgnoreUrl = (url) => {
  if (!url) return false;

  return IGNORED_URLS.some((pattern) => url.includes(pattern));
};

/**
 * Check if URL + Status Code combination should be ignored
 * @param {string} url - Request URL
 * @param {number} status - HTTP status code
 * @returns {boolean} - True if combination should be ignored
 */
export const shouldIgnoreUrlStatus = (url, status) => {
  if (!url || !status) return false;

  return IGNORED_URL_STATUS.some((pattern) => url.includes(pattern.url) && status === pattern.status);
};

/**
 * Custom error filter for Signoz
 * @param {Object} span - OpenTelemetry span
 * @returns {boolean} - True if span should be recorded
 */
export const signozErrorFilter = (span) => {
  try {
    const attributes = span.attributes || {};
    const errorMessage = attributes["exception.message"] || attributes["error.message"] || "";
    const url = attributes["http.url"] || attributes["url"] || "";
    const statusCode = attributes["http.status_code"] || attributes["status_code"];

    // Ignore if error message matches ignored patterns
    if (shouldIgnoreError(errorMessage)) {
      return false;
    }

    // Ignore if URL + Status Code combination matches ignored patterns
    if (shouldIgnoreUrlStatus(url, statusCode)) {
      console.warn(`⚠️ Filtered ${statusCode} error for ${url} (not traced to SigNoz)`);
      return false;
    }

    // Ignore if URL matches ignored patterns
    if (shouldIgnoreUrl(url)) {
      return false;
    }

    return true;
  } catch (err) {
    // If filtering fails, allow the span to be recorded
    console.warn("Error in signozErrorFilter:", err);
    return true;
  }
};

/**
 * Initialize Signoz with custom configuration
 */
export const initializeCustomSignoz = () => {
  try {
    const { initializeSignOzTracing } = require("@echoteam/signoz-react");

    // Initialize with custom configuration
    initializeSignOzTracing({
      serviceName: process.env.REACT_APP_SERVICE_NAME || "caready-customer-web",
      serverUrl: process.env.REACT_APP_SIGNOZ_URL || "https://otlphttp.signoz.echoteam.tech/v1/traces",

      // Custom error handler
      onError: (error) => {
        if (!shouldIgnoreError(error)) {
          console.error("Signoz Error:", error);
        }
      },

      // Disable automatic error tracking for specific errors
      ignoreErrors: IGNORED_ERRORS,
    });

    console.log("✅ Signoz initialized with custom configuration");
  } catch (error) {
    console.warn("⚠️ Failed to initialize Signoz:", error);
  }
};
